import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormDialog from "../../components/FormDialog";
import { createNewGroup } from "../../services/services";

const MENU_CONFIG = [
  {
    label: "Dashboard",
    children: [
      { label: "Transactions", name: "transactions", options: ["View", "Void/Refund/Chargeback"] },
    ],
  },
  {
    label: "Finance",
    // name: "finance",
    // options: ["View", "Upload file"],
    children: [
      { label: "Import Data / Payout Pipeline Manual", name: "payout_upload_file", options: ["Upload file"] },
      { label: "Payout Ingestor / Reconciliation / Settlement", name: "payout_view_data", options: ["View"] },
      { label: "Acquirer File", name: "acquirer_file", options: ["View", "Upload file"] },
      { label: "Adjustment", name: "adjustment", options: ["View", "Add", "Edit"] },
      { label: "Exception Handling", name: "exception_handling", options: ["View", "Add", "Edit"] },
      { label: "Exchange Rate", name: "exchange_rate", options: ["View", "Add", "Edit"] },
      { label: "Bank Statement", name: "bank_statement", options: ["View", "Upload file"] },
      // { label: "PayNow Duplicate", name: "paynow_duplicate", options: ["View", "Upload file"] },
      { label: "Remarks", name: "remark", options: ["Add", "Edit"] },
    ],
  },
  {
    label: "Operations",
    children: [
      { label: "Onboarding", name: "onboarding", options: ["View", "Add", "Edit", "Delete", "ComplianceCheck", "FinalApproval"] },
      { label: "Business Entity", name: "business_entity", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Merchant", name: "merchant", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Terminal", name: "terminal", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Bank Account", name: "bank_account", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Merchant Payout Info", name: "merchant_payout_info", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      // { label: "Click&Scan2Pay", name: "quick_payment", options: ["View", "Add", "Edit", "Delete"] },
      // { label: "Paynow Descriptor", name: "paynow_descriptor", options: ["View", "Add", "Assign/Query"] },
      { label: "MPI", name: "mpi", options: ["View", "Add", "Edit", "Delete"] },
      { label: "No Payout Day", name: "no_payout_day", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
    ],
  },
  {
    label: "Gateway Configuration",
    children: [
      { label: "Payment Host", name: "payment_host", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Payment Brand", name: "payment_brand", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Payment Method", name: "payment_method", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
    ],
  },
  {
    label: "Portal Management",
    children: [
      { label: "Admin User", name: "admin_user", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Admin Role", name: "admin_role", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Merchant User", name: "merchant_user", options: ["View", "Add", "Edit", "Delete", "Approve/Reject"] },
      { label: "Notifications", name: "notifications", options: ["View", "Add", "Edit", "Delete"] },
      { label: "Audit Logs", name: "audit_logs", options: ["View"] },
    ],
  },
];

const handlePermissionChange = (pageName, opt, checked, values, setFieldValue) => {

  if (pageName === "onboarding") {
    const specialOptions = ["ComplianceCheck", "FinalApproval"];
    const otherOptions = ["View", "Add", "Edit", "Delete"];
    const viewOption = ["View"];
    let newValues = [...(values[pageName] || [])];

    if (checked) {
      if (specialOptions.includes(opt)) {
        newValues = [opt, ...viewOption];
      } else {
        // When checking a regular option, remove any special options
        newValues = newValues.filter(v => !specialOptions.includes(v));
        if (!newValues.includes(opt)) {
          newValues.push(opt);
        }
      }
    } else {
      // Unchecking
      newValues = newValues.filter(v => v !== opt);
    }

    setFieldValue(pageName, newValues);
  } else {
    if (checked) {
      let newValues = [...(values[pageName] || []), opt];

      if (opt === "Approve/Reject") {
        if (!newValues.includes("View")) {
          newValues.push("View");
        }

        newValues = newValues.filter(
          (v) => v !== "Add" && v !== "Edit" && v !== "Delete"
        );
      } else if (["Add", "Edit", "Delete"].includes(opt)) {
        newValues = newValues.filter((v) => v !== "Approve/Reject");
      }

      setFieldValue(pageName, newValues);
    } else {
      let newValues = values[pageName]?.filter((v) => v !== opt) || [];
      setFieldValue(pageName, newValues);
    }
  }
};

const MenuAccordion = React.memo(({ menu, values, setFieldValue }) => {
  if (menu.children) {
    return (
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          border: "1px solid #ddd",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ "&:focus": { outline: "none" } }}
        >
          <Typography variant="h6">{menu.label}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {menu.children.map((page) => (
              <Box
                key={page.name}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #eee",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {page.label}
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  {page.options.map((opt) => (
                    <label
                      key={`${page.name}-${opt}`}
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <Checkbox
                        checked={values[page.name]?.includes(opt)}
                        onChange={(e) =>
                          handlePermissionChange(page.name, opt, e.target.checked, values, setFieldValue)
                        }
                      />
                      {opt}
                    </label>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid #ddd",
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {menu.label}
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" pl={2}>
        {menu.options.map((opt) => (
          <label
            key={`${menu.name}-${opt}`}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <Checkbox
              checked={values[menu.name]?.includes(opt)}
              onChange={(e) =>
                handlePermissionChange(menu.name, opt, e.target.checked, values, setFieldValue)
              }
            />
            {opt}
          </label>
        ))}
      </Stack>
    </Box>
  );
});

const CreateNewGroup = ({ open, onClose }) => {
  const handleSubmit = async (values, { setSubmitting }) => {
    const submitValues = { ...values, name: values.name.trim() };
    await createNewGroup(submitValues, onClose);
    setSubmitting(false);
  };

  return (
    <Container maxWidth="md">
      <Formik
        initialValues={MENU_CONFIG.reduce((acc, menu) => {
          if (menu.children) {
            menu.children.forEach((page) => (acc[page.name] = []));
          } else if (menu.options) {
            acc[menu.name] = [];
          }
          return acc;
        }, { name: "" })}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("This field is required").trim(),
        })}
        onSubmit={handleSubmit}
      >
        {({ values, touched, errors, isSubmitting, handleChange, handleBlur, setFieldValue, handleSubmit }) => (
          <FormDialog
            open={open}
            onClose={onClose}
            title="Create a New Group"
            scroll="paper"
            handleSubmit={handleSubmit}
            loading={isSubmitting}
            maxWidth="md"
          >
            <Form autoComplete="off" noValidate>
              {/* Group Name */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ width: "40%" }}>
                  Group Name:
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Name"
                  name="name"
                  autoFocus
                  color="secondary"
                  value={values.name}
                  onChange={handleChange}
                  size="small"
                  onBlur={handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  disabled={isSubmitting}
                />
              </Stack>

              {/* Permission by Menu */}
              {MENU_CONFIG.map((menu) => (
                <MenuAccordion
                  key={menu.label}
                  menu={menu}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              ))}
            </Form>
          </FormDialog>
        )}
      </Formik>
    </Container>
  );
};

export default CreateNewGroup;
